/**
 * Dynamic placeholder selector component for choosing data sources
 */

import React from 'react';
import { Badge } from '@/ui-components/ui/badge';
import { Button } from '@/ui-components/ui/button';
import { Label } from '@/ui-components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/ui-components/ui/popover';
import { Input } from '@/ui-components/ui/input';
import { ChevronDown, Plus, Database, SlidersHorizontal, WandSparkles } from 'lucide-react';

import { EntityType, MediaType, Template } from '@/page-components/post-generator/core/template.schemas';
import { entityRegistry } from '../../registry';
import { useTemplateSlots, useTemplateStore } from '../../hooks/useTemplateStore';
import { useTemplatePermissions } from '../../hooks/useTemplatePermissions';
import { getMediaTypeIcon, getMediaTypeDisplayName } from './utils';
import { ToggleGroup, ToggleGroupItem } from '@/ui-components/ui/toggle-group';
import { Tooltip, TooltipProvider, TooltipTrigger } from '@/ui-components/ui/tooltip';
import { cn } from '@/lib/utils/cn';
import { InfoCircledIcon } from '@radix-ui/react-icons';
import { collectDynamicFields } from '@/page-components/post-generator/core/runtime/resolve';
import { useMemo, useState, useEffect, useRef } from 'react';

interface DynamicSlotSelectorProps {
  /** Selected placeholder ID */
  selectedSlotId: string;
  /** Selected attribute path */
  selectedSlotAttributePath: string;
  /** Compatible media types */
  compatibleMediaTypes: MediaType[];
  /** Currently selected media type for filtering */
  selectedMediaType: MediaType;
  /** Current source kind */
  selectedSourceKind: 'slot' | 'selector' | 'override';
  /** Selected selector ID */
  selectedSelectorId: string;
  /** Selected override key */
  selectedOverrideKey: string;
  /** Whether the current user is internal */
  isInternal?: boolean;
  /** Placeholder selection handler */
  onSlotSelect: (slotId: string) => void;
  /** Attribute selection handler */
  onSlotAttributeSelect: (attributePath: string, mediaType: MediaType) => void;
  /** Placeholder creation handler */
  onCreateSlot: (entityType: EntityType) => void;
  /** Source kind change handler */
  onSourceKindChange: (kind: 'slot' | 'selector' | 'override') => void;
  /** Selector ID change handler */
  onSelectorIdChange: (selectorId: string) => void;
  /** Override key change handler */
  onOverrideKeyChange: (key: string) => void;
}

const SuggestionInput: React.FC<{
  value: string;
  onChange: (val: string) => void;
  placeholder: string;
  kind: 'selector' | 'override';
  template: Template | null;
  mediaType: MediaType;
}> = ({ value, onChange, placeholder, kind, template, mediaType }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const candidates = useMemo(() => {
    if (!template) return [];
    const fields = collectDynamicFields(template);
    const set = new Set<string>();
    
    fields.forEach(f => {
      if (f.mediaType === mediaType && f.source.kind === kind) {
        let id: string | undefined;
        if (f.source.kind === 'selector') {
          id = f.source.selectorId;
        } else if (f.source.kind === 'override') {
          id = f.source.overrideKey;
        }
        
        if (id) set.add(id);
      }
    });
    
    return Array.from(set);
  }, [template, mediaType, kind]);

  const suggestions = useMemo(() => {
    if (!value || value.trim() === '') return [];
    
    const lowerValue = value.toLowerCase();
    
    return candidates
      .filter(c => c.toLowerCase().includes(lowerValue) && c !== value)
      .sort((a, b) => {
        // Simple ranking: exact start > contains
        const aStarts = a.toLowerCase().startsWith(lowerValue);
        const bStarts = b.toLowerCase().startsWith(lowerValue);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        return a.localeCompare(b);
      })
      .slice(0, 3);
  }, [candidates, value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={wrapperRef}>
      <Input
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder}
        className="h-6 text-xs"
      />
      {isOpen && suggestions.length > 0 && (
        <div className="absolute bottom-full left-0 w-full mb-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 overflow-hidden">
           <div className="text-[10px] bg-gray-50 px-2 py-1 text-gray-500 font-medium border-b border-gray-100">
             Vorschläge
           </div>
           {suggestions.map(s => (
             <button
               key={s}
               className="w-full text-left px-2 py-1.5 text-xs hover:bg-blue-50 hover:text-blue-600 transition-colors truncate"
               onClick={() => {
                 onChange(s);
                 setIsOpen(false);
               }}
             >
               {s}
             </button>
           ))}
        </div>
      )}
    </div>
  );
};

/**
 * Selector for choosing dynamic data sources (placeholders and attributes)
 */
export const DynamicSourceSelector: React.FC<DynamicSlotSelectorProps> = ({
  selectedSlotId,
  selectedSlotAttributePath,
  compatibleMediaTypes,
  selectedMediaType,
  selectedSourceKind,
  selectedSelectorId,
  selectedOverrideKey,
  isInternal,
  onSlotSelect,
  onSlotAttributeSelect,
  onCreateSlot,
  onSourceKindChange,
  onSelectorIdChange,
  onOverrideKeyChange
}) => {
  const allSlots = useTemplateSlots();
  const template = useTemplateStore(state => state.template);
  
  const { canEditTemplate, canManageStore } = useTemplatePermissions();
  const allowCreateSlot = canEditTemplate;
  const allowOverride = canManageStore;

  // Filter placeholders that have at least one attribute matching the selected media type
  const compatibleSlots = allSlots.filter(slot => {
    const entity = entityRegistry.get(slot.entityType);
    return entity?.attributes.some(attr => attr.mediaType === selectedMediaType);
  });

  // Get entities that can create placeholders with attributes matching the selected media type
  const compatibleEntities = entityRegistry.getAll().filter(entity => 
    entity.attributes.some(attr => attr.mediaType === selectedMediaType)
  );

  const renderSlotSelection = () => {
    if (compatibleSlots.length === 0) {
      return (
        <div className="text-center py-4 space-y-3">
          <div className="text-xs text-gray-500">
            Keine Platzhalter mit {getMediaTypeDisplayName(selectedMediaType)}-Attributen vorhanden
          </div>
          <div className="space-y-2">
            <div className="text-xs text-gray-600 font-medium">Platzhalter erstellen für:</div>
            <div className="grid grid-cols-2 gap-1">
              {compatibleEntities.map((entity) => (
                <Button
                  key={entity.type}
                  variant="outline"
                  size="sm"
                  onClick={() => allowCreateSlot && onCreateSlot(entity.type as EntityType)}
                  className="h-7 text-xs"
                  disabled={!allowCreateSlot}
                >
                  <Plus className="w-3 h-3 mr-1" />
                  {entity.displayName}
                </Button>
              ))}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {/* Placeholder Selection */}
        <div className="space-y-1">
          <Label className="text-xs font-medium text-gray-700">Platzhalter auswählen</Label>
          <div className="grid gap-1">
            {compatibleSlots.map((slot) => (
              <Button
                key={slot.id}
                variant={selectedSlotId === slot.id ? "outline" : "ghost"}
                size="sm"
                onClick={() => onSlotSelect(slot.id)}
                className={cn(
                  "w-full justify-start h-7 text-xs",
                  selectedSlotId === slot.id ? "bg-blue-50 border-blue-200 text-blue-700" : ""
                )}
              >
                <span className="mr-2">{entityRegistry.getEntityIcon(slot.entityType, "w-3 h-3")}</span>
                {slot.name}
              </Button>
            ))}
          </div>
        </div>

        {/* Attribute Selection */}
        {selectedSlotId && (
          <div className="space-y-1">
            <Label className="text-xs font-medium text-gray-700">Attribut auswählen</Label>
            <div className="grid gap-1">
              {(() => {
                const slot = allSlots.find(s => s.id === selectedSlotId);
                if (!slot) return null;
                
                const entity = entityRegistry.get(slot.entityType);
                if (!entity) return null;

                // Only show attributes that match the selected media type
                const filteredAttributes = entity.attributes.filter(attr => 
                  attr.mediaType === selectedMediaType
                );

                if (filteredAttributes.length === 0) {
                  return (
                    <div className="text-xs text-gray-500 py-2 text-center">
                      Keine {getMediaTypeDisplayName(selectedMediaType)}-Attribute verfügbar
                    </div>
                  );
                }

                return filteredAttributes.map((attr) => (
                  <Button
                    key={attr.attributeKey}
                    variant={selectedSlotAttributePath === attr.attributeKey ? "outline" : "ghost"}
                    size="sm"
                    onClick={() => onSlotAttributeSelect(attr.attributeKey, attr.mediaType)}
                    className={cn(
                      "w-full justify-start h-7 text-xs",
                      selectedSlotAttributePath === attr.attributeKey ? "bg-blue-50 border-blue-200 text-blue-700" : ""
                    )}
                  >
                    {getMediaTypeIcon(attr.mediaType)}
                    <span className="ml-2">{attr.attributeLabel}</span>
                    <Badge variant="secondary" className="ml-auto text-xs">
                      {getMediaTypeDisplayName(attr.mediaType)}
                    </Badge>
                  </Button>
                ));
              })()}
            </div>
          </div>
        )}

        {/* Quick Create Placeholder */}
        {allowCreateSlot && (
        <div className="pt-2 border-t">
          <div className="text-xs text-gray-600 font-medium mb-1">Neuen Platzhalter erstellen:</div>
          <div className="grid grid-cols-2 gap-1">
            {compatibleEntities.map((entity) => (
              <Button
                key={entity.type}
                variant="outline"
                size="sm"
                onClick={() => onCreateSlot(entity.type as EntityType)}
                className="h-7 text-xs"
              >
                <Plus className="w-3 h-3 mr-1" />
                {entity.displayName}
              </Button>
            ))}
          </div>
        </div>
        )}
        <div className="text-[11px] text-gray-500">
          <InfoCircledIcon className="w-4 h-4 inline-block mr-1" />
          Platzhalter binden ein Entitätsattribut (z. B. Mannschaft→Name); nach Auswahl der Entität im Post-Generator wird der passende Wert automatisch übernommen.
        </div>
      </div>
    );
  };

  const renderSelectorConfig = () => {
    return (
      <div className="space-y-2">
        <Label className="text-xs font-medium text-gray-700">Auswahl-ID</Label>
        <SuggestionInput
          value={selectedSelectorId}
          onChange={onSelectorIdChange}
          placeholder="z. B. headline, heroImage, sponsorLogo"
          kind="selector"
          template={template}
          mediaType={selectedMediaType}
        />
        <div className="text-[11px] text-gray-500">
          <InfoCircledIcon className="w-4 h-4 inline-block mr-1" />
          Diese Auswahl (Text, Bild, Datum, ...) wird später im Post-Generator manuell gesetzt.
        </div>
      </div>
    );
  };

  const renderOverrideConfig = () => {
    return (
      <div className="space-y-2">
        <Label className="text-xs font-medium text-gray-700">Override-Schlüssel</Label>
        <SuggestionInput
          value={selectedOverrideKey}
          onChange={onOverrideKeyChange}
          placeholder="z. B. clubName, brandColor, crestImage"
          kind="override"
          template={template}
          mediaType={selectedMediaType}
        />
        <div className="text-[11px] text-gray-500">
          <InfoCircledIcon className="w-4 h-4 inline-block mr-1" />
          Wird nur beim Import aus dem Template-Store genutzt und in statische Werte umgewandelt.
        </div>
      </div>
    );
  };

  const selectedSlot = selectedSlotId ? allSlots.find(s => s.id === selectedSlotId) : null;
  const selectedSlotName = selectedSlot?.name || 'Platzhalter wählen...';
  const selectedSlotIcon = selectedSlot ? entityRegistry.getEntityIcon(selectedSlot.entityType, "w-3 h-3") : entityRegistry.getEntityIcon('Unknown' as EntityType, "w-3 h-3");

  let selectedAttributeLabel = '';
  if (selectedSlot && selectedSlotAttributePath) {
    const entity = entityRegistry.get(selectedSlot.entityType);
    if (entity) {
      const attr = entity.attributes.find(a => a.attributeKey === selectedSlotAttributePath);
      if (attr) {
        selectedAttributeLabel = attr.attributeLabel;
      }
    }
  }

  return (
    <div>
      <Label className="text-xs text-gray-500 mb-1 block">Datenquelle</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            className="w-full h-6 text-xs justify-between"
            size="sm"
          >
            <span className="flex items-center gap-1 overflow-hidden">
              {selectedSourceKind === 'slot' ? (
                <>
                  {selectedSlotIcon}
                  <span className="truncate font-medium">{selectedSlotName}</span>
                  {selectedAttributeLabel && (
                    <span className="text-gray-400 truncate font-normal">({selectedAttributeLabel})</span>
                  )}
                </>
              ) : selectedSourceKind === 'selector' ? (
                <>
                  <SlidersHorizontal className="w-3 h-3" />
                  {selectedSelectorId ? selectedSelectorId : 'Auswahl-ID setzen...'}
                </>
              ) : isInternal ? (
                <>
                  <WandSparkles className="w-3 h-3" />
                  {selectedOverrideKey ? selectedOverrideKey : 'Override-Schlüssel setzen...'}
                </>
              ) : (
                <>
                  <SlidersHorizontal className="w-3 h-3" />
                  {selectedSelectorId ? selectedSelectorId : 'Auswahl-ID setzen...'}
                </>
              )}
            </span>
            <ChevronDown className="w-3 h-3" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-2">
          <div className="space-y-2">
            <TooltipProvider>
              <ToggleGroup type="single" value={selectedSourceKind} onValueChange={(v) => v && onSourceKindChange(v as 'slot' | 'selector' | 'override')} className="w-full">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <ToggleGroupItem
                      value="slot"
                      variant="outline"
                      size="sm"
                      data-selected={selectedSourceKind === 'slot' ? 'true' : undefined}
                      className="h-7 flex-1 data-[selected=true]:!bg-blue-50 data-[selected=true]:!text-blue-700 data-[selected=true]:!border-blue-200"
                    >
                      <Database className="w-3 h-3" />
                      <span className="text-xs">Platzhalter</span>
                    </ToggleGroupItem>
                  </TooltipTrigger>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <ToggleGroupItem
                      value="selector"
                      variant="outline"
                      size="sm"
                      data-selected={selectedSourceKind === 'selector' ? 'true' : undefined}
                      className="h-7 flex-1 data-[selected=true]:!bg-blue-50 data-[selected=true]:!text-blue-700 data-[selected=true]:!border-blue-200"
                    >
                      <SlidersHorizontal className="w-3 h-3" />
                      <span className="text-xs">Auswahl</span>
                    </ToggleGroupItem>
                  </TooltipTrigger>
                </Tooltip>
                {isInternal && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <ToggleGroupItem
                        value="override"
                        variant="outline"
                        size="sm"
                        data-selected={selectedSourceKind === 'override' ? 'true' : undefined}
                        className="h-7 flex-1 data-[selected=true]:!bg-blue-50 data-[selected=true]:!text-blue-700 data-[selected=true]:!border-blue-200"
                      >
                        <WandSparkles className="w-3 h-3" />
                        <span className="text-xs">Overrides</span>
                      </ToggleGroupItem>
                    </TooltipTrigger>
                  </Tooltip>
                )}
              </ToggleGroup>
            </TooltipProvider>
            <div className="pt-2 border-t max-h-[300px] overflow-y-auto">
              {selectedSourceKind === 'slot' && renderSlotSelection()}
              {selectedSourceKind === 'selector' && renderSelectorConfig()}
              {selectedSourceKind === 'override' && allowOverride && renderOverrideConfig()}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
